import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export const VideoModal = ({ video, onClose }) => {
  useEffect(() => {
    if (!video) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [video, onClose]);

  return (
    <AnimatePresence>
      {video && (
        <motion.div
          data-testid="video-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-10 bg-[var(--ink)]/85 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-5xl bg-white border-[1.5px] border-[var(--ink)] p-2 md:p-3"
            style={{ boxShadow: "8px 8px 0 var(--yellow)" }}
            initial={{ scale: 0.9, rotate: -2, y: 30 }}
            animate={{ scale: 1, rotate: 0, y: 0 }}
            exit={{ scale: 0.9, rotate: 2, y: 30 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video bg-black">
              <iframe
                data-testid="video-iframe"
                src={video.youtubeId
                  ? `https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0`
                  : `https://player.vimeo.com/video/${video.id}?autoplay=1&title=0&byline=0&portrait=0`}
                className="w-full h-full"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.title}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 px-1 pt-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-hand text-2xl">{video.title}</span>
                <span className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-[var(--ink-60)]">{video.role}</span>
              </div>
              <a href={video.url} target="_blank" rel="noopener noreferrer" className="font-mono text-[0.62rem] uppercase tracking-[0.15em] underline underline-offset-2">Open video ↗</a>
            </div>
            <button
              data-testid="video-modal-close"
              onClick={onClose}
              className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-[var(--pink)] border-[1.5px] border-[var(--ink)] hard-shadow flex items-center justify-center hover:rotate-90 transition-transform"
              aria-label="Close video"
            >
              <X size={16} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
